import { Component, OnInit } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { HOUR24_DATE_CONFIG } from 'src/app/calvin-emr-shared/global';
import { PatientDashboardStore } from 'src/app/patients/state/dashboard-page.store';
import { PatientDashboardTabComponent } from 'src/app/patients/dashboard-page/dashboard-tab/dashboard-tab.component';
import { Assessment } from 'src/app/calvin-emr-shared/models/assessment.model';
import { Patient } from 'src/app/calvin-emr-shared/models/patient.model';
import { Store } from '@ngrx/store';

// For downloading assessment history
import { MenuModule } from 'primeng/menu';
import { PanelModule } from 'primeng/panel';
import { AccordionModule } from 'primeng/accordion';
import { GeneralAppearanceInfoComponent } from './general-appearance-info/general-appearance-info.component';
import { HEENTInfoComponent } from './heent-info/heent-info.component';
import { CardiovascularInfoComponent } from './cardiovascular-info/cardiovascular-info.component';
import { RespiratoryInfoComponent } from './respiratory-info/respiratory-info.component';
import { NeurologicalInfoComponent } from './neurological-info/neurological-info.component';
import { MusculoskeletalInfoComponent } from './musculoskeletal-info/musculoskeletal-info.component';
import { GastrointestinalInfoComponent } from './gastrointestinal-info/gastrointestinal-info.component';
import { GenitourinaryInfoComponent } from './genitourinary-info/genitourinary-info.component';
import { PsychosocialInfoComponent } from './psychosocial-info/psychosocial-info.component';
import { IntegumentaryInfoComponent } from './integumentary-info/integumentary-info.component';
import { PainAssessmentInfoComponent } from './pain-assessment-info/pain-assessment-info.component';
import { IntakeOutputInfoComponent } from './intake-output-info/intake-output-info.component';
import { ProviderNotifiedInfoComponent } from './provider-notified-info/provider-notified-info.component';
import { ExpansionPanelComponent } from "../../../shared/expansion-panel/expansion-panel.component";
import { PATIENT_TYPE_OBSTETRIC, PATIENT_TYPE_PEDIATRIC } from 'src/app/global';
import { AssessmentPdfGenerator } from './assessment-to-pdf';

@Component({
  selector: 'app-assessments-history-tab',
  templateUrl: './assessments-history-tab.component.html',
  styleUrls: ['./assessments-history-tab.component.scss'],
  standalone: true,
  imports: [
    MenuModule,
    PanelModule,
    AccordionModule,
    GeneralAppearanceInfoComponent,
    HEENTInfoComponent,
    CardiovascularInfoComponent,
    RespiratoryInfoComponent,
    NeurologicalInfoComponent,
    MusculoskeletalInfoComponent,
    GastrointestinalInfoComponent,
    GenitourinaryInfoComponent,
    PsychosocialInfoComponent,
    IntegumentaryInfoComponent,
    PainAssessmentInfoComponent,
    IntakeOutputInfoComponent,
    ProviderNotifiedInfoComponent,
    ExpansionPanelComponent
  ],
})
export class AssessmentsHistoryTabComponent extends PatientDashboardTabComponent implements OnInit {
  assessments: Assessment[] = [];
  menuItems: MenuItem[] = [];
  selectedAssessment: Assessment = new Assessment();
  patient!: Patient;
  PATIENT_TYPE_OBSTETRIC = PATIENT_TYPE_OBSTETRIC;

  constructor(patientStore: PatientDashboardStore, store: Store) {
    super(patientStore, store);
    this.patient$.subscribe((patient: Patient) => {
      this.patient = patient;
      this.assessments = patient.assessments;

      // Sort so new assessments are at the bottom of the list.
      this.assessments.sort((a, b) => {
        return a.date.toDate().getTime() - b.date.toDate().getTime();
      });

      this.menuItems = this.assessments.map((assessment) => {
        return {
          label: this.getAssessmentDateString(assessment),
          command: (event) => {
            // @ts-ignore
            if (event.originalEvent.target.classList.contains('pi-download')) {
              this.downloadAssessment(assessment, this.patient);
            } else {
              this.selectedAssessment = assessment;
            }
          },
          icon: 'pi pi-download',
          iconPos: 'right',
        };
      });

      if (this.hasAssessments) {
        this.selectedAssessment = this.assessments[0];
      }
    });
  }

  ngOnInit(): void { }

  getAssessmentDateString(assessment: Assessment) {
    return assessment.date.toDate().toLocaleString([], HOUR24_DATE_CONFIG).replace(',', ' @');
  }

  downloadAssessment(assessment: Assessment, patient: Patient): void {
    const pdfGen = new AssessmentPdfGenerator(assessment, patient);
    const pdf = pdfGen.getResult();
    const parsedDate = this.getAssessmentDateString(assessment).split(' @ ')[0];
    const fileName = `${this.patient.lastName}_${this.patient.firstName}_Assessment(${parsedDate}).pdf`;

    // const pdfDataUri = pdf.output('dataurlstring');
    // const newWindow = window.open();
    // if (newWindow) {
    //     newWindow.document.write(`<iframe width='100%' height='100%' src='${pdfDataUri}'></iframe>`);
    // } else {
    //     console.error("Unable to open print preview window.");
    // }
    pdf.save(fileName);
  }

  get hasAssessments(): boolean {
    return this.assessments.length > 0;
  }

  get selectedAssessmentDateString(): string {
    if (this.hasAssessments && this.selectedAssessment) {
      return this.selectedAssessment?.date
        .toDate()
        .toLocaleString([], HOUR24_DATE_CONFIG)
        .replace(',', ' at');
    }
    return 'NULL';
  }

  get generalAppearance() {
    return this.selectedAssessment.generalAppearance;
  }

  get heent() {
    return this.selectedAssessment.heent;
  }

  get cardiovascular() {
    return this.selectedAssessment.cardiovascular;
  }

  get respiratory() {
    return this.selectedAssessment.respiratory;
  }

  get neurological() {
    return this.selectedAssessment.neurological;
  }

  get musculoskeletal() {
    return this.selectedAssessment.musculoskeletal;
  }

  get gastrointestinal() {
    return this.selectedAssessment.gastrointestinal;
  }

  get genitourinary() {
    return this.selectedAssessment.genitourinary;
  }

  get psychosocial() {
    return this.selectedAssessment.psychosocial;
  }

  get integumentary() {
    return this.selectedAssessment.integumentary;
  }

  get painAssessment() {
    return this.selectedAssessment.painAssessment;
  }

  get intakeOutput() {
    return this.selectedAssessment.intakeOutput;
  }

  get providerNotified() {
    return this.selectedAssessment.providerNotified;
  }
}
